'use strict';

import * as debounce from 'lodash/debounce';
import VZSpinner from 'Components/VZSpinner';
import { CONST, addEvent, removeChildNodes, removeAccentCharacters } from 'Util/util';

const selectors = {
    mainContainer: '.js-bscan-main',
    searchStep: '.js-search-company-step',
    searchField: '.js-search-company',
    resultsBox: '.js-search-company-results',
    resultsBoxContainer: '.js-search-company-results-container',
    resultsBoxHeading: '.js-search-company-results-heading',
    resultsBoxList: '.js-search-company-results-list',
    resultsBoxItem: '.js-search-company-results-list .js-btn-next',
    resultsBoxFadeout: '.js-search-company-results-fadeout'
};

const classes = {
    inputFieldClear: 'input-clear',
    jsInputFieldClear: 'js-input-clear'
};

const attributes = {
    stepTarget: 'data-bscan-step-target',
    minQueryLengthNumber: 'data-min-query-length-number',
    minQueryLengthText: 'data-min-query-length-text'
};

const spinner = document.querySelector('vz-spinner') || new VZSpinner();

// Global scope
let mainContainer;
let searchStep;
let stepTarget;
let form;
let resultsBox;
let minQueryLengthNumber;
let minQueryLengthText;
let searchEndpoint;

// Variables for updateFadeOut
let resultsBoxContainer;
let resultsBoxHeading;
let resultsBoxList;
let resultsBoxFadeout;

let resultsBoxHeight;
let resultsBoxChildrenHeight;
let resultsBoxFadeoutHeight;

/**
 * @private calculateFadeOutHeight
 *
 * @description
 * Here we calculate at which point during scrolling, the fadeout should move away as we approach the end
 */
function calculateFadeOutHeight() {
    const scrollPos = resultsBoxContainer.scrollTop;
    const pointOfMovingFadeOutAway = resultsBoxChildrenHeight - resultsBoxHeight - resultsBoxFadeoutHeight;

    if (scrollPos - pointOfMovingFadeOutAway > 0) {
        resultsBoxFadeout.style.maxHeight = `${resultsBoxFadeoutHeight - (scrollPos - pointOfMovingFadeOutAway)}px`;
    } else {
        resultsBoxFadeout.style.maxHeight = 'none';
    }
}

/**
 * @private setFadeOutStyling
 */
function setFadeOutStyling() {
    resultsBoxHeight = resultsBoxContainer.clientHeight;
    resultsBoxChildrenHeight = resultsBoxHeading.clientHeight + resultsBoxList.clientHeight;

    // If we have a scrollable resultsBox
    if (resultsBoxChildrenHeight > resultsBoxHeight) {
        resultsBoxFadeout.classList.remove(CONST.classes.dNone);
        resultsBoxFadeout.style.width = `${resultsBoxContainer.clientWidth}px`;
        resultsBoxFadeoutHeight = resultsBoxFadeout.clientHeight; // After displaying the fadeout element, retrieve the height

        resultsBoxContainer.addEventListener(CONST.events.scroll, updateFadeOut); // eslint-disable-line no-use-before-define
        calculateFadeOutHeight();
    } else {
        resultsBoxFadeout.classList.add(CONST.classes.dNone);
    }
}

/**
 * @private updateFadeOut
 * @param {event} event - The scroll event
 *
 * @description
 * Will update the position and visibility of the fadeout element
 */
function updateFadeOut(event) {
    // If we don't have the event, it's the initial setup
    if (!event) {
        resultsBoxContainer = document.querySelector(selectors.resultsBoxContainer);
        resultsBoxFadeout = document.querySelector(selectors.resultsBoxFadeout);
        resultsBoxHeading = document.querySelector(selectors.resultsBoxHeading);
        resultsBoxList = document.querySelector(selectors.resultsBoxList);

        setFadeOutStyling();
        window.addEventListener(CONST.events.resize, setFadeOutStyling);
    } else {
        // If we do have the event, we're scrolling
        calculateFadeOutHeight();
    }
}

/**
 * @private searchCompany
 * @param {Event} event - The change event
 */
function searchCompany(event) {
    const searchField = event.target;
    const csrfToken = window.csrf.token;
    const csrfName = window.csrf.tokenName;
    form = form || searchField.closest(CONST.selectors.form.form);
    mainContainer = mainContainer || searchField.closest(selectors.mainContainer);
    searchStep = searchStep || searchField.closest(selectors.searchStep);
    stepTarget = stepTarget || searchStep.getAttribute(attributes.stepTarget);
    resultsBox = resultsBox || (form && form.querySelector(selectors.resultsBox));

    if (!form || !resultsBox) {
        console.error('fn searchCompany: form or resultsBox not found');
        return;
    }

    minQueryLengthNumber = minQueryLengthNumber || parseInt(mainContainer.getAttribute(attributes.minQueryLengthNumber), 10);
    minQueryLengthText = minQueryLengthText || parseInt(mainContainer.getAttribute(attributes.minQueryLengthText), 10);
    searchEndpoint = searchEndpoint || form.getAttribute(CONST.attributes.action);

    const query = removeAccentCharacters(searchField.value);
    const starsWithNumber = Number.isInteger(parseInt(query.charAt(0), 10));
    const minSearchQueryLength = starsWithNumber ? minQueryLengthNumber : minQueryLengthText;

    if (query.length >= minSearchQueryLength) {
        const formData = new FormData();
        formData.append(csrfName, csrfToken);

        spinner.start();
        fetch(`${searchEndpoint}?q=${query}`, {
            method: 'POST',
            body: formData
        })
            .then((res) => res.json())
            .then((response) => {
                if (response.success) {
                    resultsBox.innerHTML = response.html;

                    searchStep.querySelectorAll(selectors.resultsBoxItem).forEach((item) => item.setAttribute(attributes.stepTarget, stepTarget));

                    // Init code for the fadeout
                    updateFadeOut();
                } else {
                    removeChildNodes(resultsBox);
                    document.dispatchEvent(
                        new CustomEvent(CONST.events.responseError, {
                            detail: JSON.stringify(response)
                        })
                    );
                }
            })
            .finally(spinner.stop());
    } else {
        removeChildNodes(resultsBox);
    }
}

/**
 * @private clearInputField
 * @param {event} event - The event
 */
function clearInputField(event) {
    const clearInputElement = event.target;
    const inputToClear = clearInputElement.previousElementSibling;

    form = form || clearInputElement.closest(CONST.selectors.form.form);
    resultsBox = resultsBox || (form && form.querySelector(selectors.resultsBox));

    if (inputToClear) {
        inputToClear.value = '';
        inputToClear.focus();
        inputToClear.classList.add(CONST.classes.empty);
        clearInputElement.parentNode.removeChild(clearInputElement);
    }

    if (resultsBox) {
        resultsBox.innerHTML = '';
    }
}

/**
 * @private addClearInputFieldElement
 * @param {event} event - The event
 */
function addClearInputFieldElement(event) {
    const searchField = event.target;
    form = form || searchField.closest(CONST.selectors.form.form);

    if (!form) {
        return;
    }

    if (searchField.value.length > 0) {
        // Only add the clearInputElement element if there isn't one yet
        if (!form.querySelector(CONST.selectors.form.inputFieldClear)) {
            const clearInputElement = document.createElement('span');
            clearInputElement.classList.add(classes.inputFieldClear, classes.jsInputFieldClear);

            searchField.insertAdjacentElement('afterend', clearInputElement);
            clearInputElement.addEventListener(CONST.events.click, clearInputField);
        }
    } else {
        searchField.classList.add(CONST.classes.empty);
    }
}

addEvent(document, CONST.events.keyUp, selectors.searchField, debounce(searchCompany, 500));
addEvent(document, CONST.events.keyUp, selectors.searchField, addClearInputFieldElement);
